// Bandeja -> Casos (D37): barra de filtros (en todas las vistas), Mi dia, la lista de una vista y, para el
// administrador, los casos de grupos que no existen en el CRM con acciones en lote.
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { aCaso, actualizarCasos, type Caso, editarCaso, ESTADOS_CASO, type RawCaso } from '../../api/casos';
import { buscarEnCasos } from '../../api/busqueda';
import { borrarVista, contarVista, crearVista, paginaDeVista } from '../../api/vistasCaso';
import { useSession } from '../../auth/session';
import { useMiembros } from '../../data/queries';
import { ago } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { type FiltrosCasos, ESTADOS_FILTRO, pasaFiltros, type Senal, senalDe, textoSenal } from '../../lib/senalesCaso';
import { ESTADOS_ABIERTOS, type VistaCaso } from '../../lib/vistasCaso';
import { Empty, ErrorBox, Pill, Skeleton } from '../../ui/bits';
import { Dialog } from '../../ui/Dialog';
import { toast } from '../../ui/toasts';
import { actividadDe, sinVer, useVistos } from '../../lib/vistosCaso';
import { quitarCaso } from '../../lib/pestanasCaso';
import { abrirAsistente } from '../../ui/AsistenteFlotante';
import { refrescarCasos, useCasosSinCerrar, useFiltros, useMiDia, useMisGrupos, useOrdenCasos, type VistaElegida, VISTA_SIN_CERRAR, VISTA_TODOS } from './useVistas';
import { type CampoOrden, type OrdenCasos, paginasVisibles, rangoPagina, siguienteOrden, TAMANOS, totalPaginas } from '../../lib/ordenCasos';
import { despejar, destinoCasos } from '../../lib/vuelo';
import { BandejaDespejada } from '../../ui/BandejaDespejada';
import { useKey } from '../../hooks';
import { phone } from '../../phone/store';

const nombreEstado = (e: Caso['estado']) => ESTADOS_CASO.find(([v]) => v === e)?.[1] ?? e;
const hayFiltros = (f: FiltrosCasos) => !!(f.q || f.grupo || f.estado || f.quien);

/* ---------------- barra de filtros ---------------- */

export function BarraFiltros({ etiquetaTodos = 'Sin cerrar' }: { etiquetaTodos?: string }) {
  const [f, cambiar] = useFiltros();
  const grupos = useMisGrupos();
  const [q, setQ] = useState(f.q);
  const [guardando, setGuardando] = useState(false);
  // La busqueda se aplica tras una pausa corta mientras se escribe.
  useEffect(() => setQ(f.q), [f.q]);
  useEffect(() => {
    if (q === f.q) return;
    const t = setTimeout(() => cambiar({ q: q.trim() }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const seg = <T extends string>(valor: T, opciones: [T, string][], clave: 'estado' | 'quien') => (
    <div className="seg" role="group" aria-label={clave === 'estado' ? 'Estado' : 'Quién'}>
      {opciones.map(([v, t]) => (
        <button key={v || 'todos'} type="button" aria-pressed={valor === v} onClick={() => cambiar({ [clave]: v })}>
          {t}
        </button>
      ))}
    </div>
  );
  return (
    <div className="casos-filtros">
      <label className="field casos-q">
        <Icon name="search" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en los casos: palabras, número, cliente…" aria-label="Buscar casos" />
      </label>
      {grupos.nombres.length > 1 ? (
        <select className="inp casos-grupo" value={f.grupo} onChange={(e) => cambiar({ grupo: e.target.value })} aria-label="Grupo">
          <option value="">Todos mis grupos</option>
          {grupos.nombres.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      ) : null}
      {seg(
        f.estado,
        [
          ['', etiquetaTodos],
          ['nuevos', 'Nuevos'],
          ['abiertos', 'Abiertos'],
          ['pendientes', 'Esperando cliente'],
          ['resueltos', 'Resueltos'],
        ],
        'estado',
      )}
      {seg(
        f.quien,
        [
          ['', 'Todos'],
          ['mios', 'Míos'],
          ['sintomar', 'Sin tomar'],
        ],
        'quien',
      )}
      {hayFiltros(f) ? (
        <>
          <button type="button" className="btn sm ghost" onClick={() => (setQ(''), cambiar({ q: '', grupo: '', estado: '', quien: '' }))}>
            Limpiar
          </button>
          <button type="button" className="btn sm" onClick={() => setGuardando(true)}>
            <Icon name="star" /> Guardar como vista
          </button>
        </>
      ) : null}
      {guardando ? <GuardarVista filtros={f} onClose={() => setGuardando(false)} /> : null}
    </div>
  );
}

// "Guardar como vista": queda en el servidor (objeto vistaCaso) solo para quien la crea, en la columna de la Bandeja.
function GuardarVista({ filtros: f, onClose }: { filtros: FiltrosCasos; onClose: () => void }) {
  const { yo } = useSession();
  const qc = useQueryClient();
  const ir = useNavigate();
  const [nombre, setNombre] = useState(
    [
      f.grupo,
      f.estado && { nuevos: 'nuevos', abiertos: 'abiertos', pendientes: 'esperando cliente', resueltos: 'resueltos' }[f.estado],
      f.quien && { mios: 'míos', sintomar: 'sin tomar' }[f.quien],
      f.q && `"${f.q}"`,
    ]
      .filter(Boolean)
      .join(' · ') || 'Mi vista',
  );
  const [enviando, setEnviando] = useState(false);
  const guardar = async () => {
    if (!nombre.trim() || !yo.miembroId) return;
    setEnviando(true);
    try {
      const v = await crearVista({
        nombre: nombre.trim(),
        orden: 100,
        estados: f.estado ? ESTADOS_FILTRO[f.estado] : ESTADOS_ABIERTOS,
        grupos: f.grupo ? [f.grupo] : [],
        gruposExcluidos: [],
        etiquetas: [],
        asignado: f.quien === 'mios' ? 'yo' : f.quien === 'sintomar' ? 'nadie' : '',
        tituloContiene: f.q,
        miembros: [yo.miembroId],
        activa: true,
      });
      await qc.invalidateQueries({ queryKey: ['vistas-caso'] });
      toast(`Vista "${nombre.trim()}" guardada: queda en la columna de la Bandeja.`);
      const id = (v as { id?: string } | undefined)?.id;
      // El orden y el tamano de pagina elegidos siguen en la vista nueva.
      const actual = new URLSearchParams(window.location.search);
      const sigue = new URLSearchParams({ vista: id ?? '' });
      for (const k of ['orden', 'dir', 'tam']) if (actual.get(k)) sigue.set(k, actual.get(k) as string);
      if (id) ir(`/bandeja/casos?${sigue.toString()}`);
      onClose();
    } catch (e) {
      toast((e as Error).message, 'error');
      setEnviando(false);
    }
  };
  return (
    <Dialog titulo="Guardar como vista" subtitulo="Queda en la columna de la Bandeja, solo para ti, con su número de casos." onClose={onClose} width={460}>
      <form className="form" onSubmit={(e) => (e.preventDefault(), void guardar())}>
        <label>
          Nombre
          <input className="inp" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus maxLength={60} />
        </label>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={!nombre.trim() || enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ---------------- piezas de un caso ---------------- */

function AvisoCaso({ c, s }: { c: Caso; s: Senal | null }) {
  if (!s) return null;
  return (
    <span className={`senal s-${s}`}>
      <span className="punto" aria-hidden="true" />
      {textoSenal(c, s)}
    </span>
  );
}

const quienEs = (c: Caso, nombre: (id: string) => string) => (c.asignadoId ? nombre(c.asignadoId) : c.duenoZammad ? `de ${c.duenoZammad.split('@')[0]}` : 'Sin tomar');
const clienteDe = (c: Caso) => c.empresa?.nombre || c.persona?.nombre || c.correoCliente || 'Sin cliente';

function useNombreMiembro() {
  const miembros = useMiembros();
  return (id: string) => miembros.data?.find((m) => m.id === id)?.nombre ?? '—';
}

// Tomar un caso: queda asignado a quien lo toma (y si era Nuevo, pasa a Abierto).
function useTomar() {
  const qc = useQueryClient();
  const { yo } = useSession();
  return async (c: Caso) => {
    if (!yo.miembroId) return;
    const cambios = { asignadoId: yo.miembroId, ...(c.estado === 'NUEVO' ? { estado: 'ABIERTO' as const } : {}) };
    qc.setQueriesData<Caso[]>({ queryKey: ['casos-vista'] }, (xs) => xs?.map((x) => (x.id === c.id ? { ...x, ...cambios } : x)));
    try {
      await editarCaso(c.id, cambios);
      toast(`Tomaste el caso #${c.numero}.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
    refrescarCasos(qc);
  };
}

// Ultima actividad del caso (D42): cuando y quien ("hace 5 min · cliente"), y "Nuevo" si no lo has visto.
// `creado`: con la lista ordenada por fecha de creacion se muestra esa fecha.
function UltimaActividad({ c, nuevo, creado = false }: { c: Caso; nuevo: boolean; creado?: boolean }) {
  if (creado)
    return (
      <time className="ultima" title={`Creado: ${new Date(c.creado).toLocaleString('es-CO')}`}>
        <span className="ultima-cuando">{ago(c.creado)}</span>
        <span className="ultima-quien">creado</span>
      </time>
    );
  const a = actividadDe(c);
  return (
    <time className={`ultima ${nuevo ? 'nuevo' : ''}`} title={a.fecha ? `Última actividad: ${new Date(a.fecha).toLocaleString('es-CO')}` : undefined}>
      <span className="ultima-cuando">
        {nuevo ? <span className="nuevo-pill">Nuevo</span> : null}
        {ago(a.fecha)}
      </span>
      {a.quien ? <span className="ultima-quien">{a.quien === 'cliente' ? 'escribió el cliente' : 'respondió el equipo'}</span> : null}
    </time>
  );
}

/* ---------------- acciones en lote (D53) ---------------- */

// Cerrar o tomar varios casos a la vez (Mi dia y las vistas). Cerrar pide confirmacion y no avisa al cliente, igual
// que "Cerrar" dentro del caso (el aviso al cliente es solo al Resolver).
// Al cerrar, cada caso vuela a su contador y su fila se pliega (solo animacion; los datos no cambian).
async function despejarCerrados(qc: ReturnType<typeof useQueryClient>, ids: string[], salen: boolean) {
  await despejar(ids, destinoCasos(), { pliega: salen });
  if (salen) qc.setQueriesData<Caso[]>({ queryKey: ['casos-vista'] }, (xs) => (Array.isArray(xs) ? xs.filter((c) => !ids.includes(c.id)) : xs));
}

function useEnLote(sel: Set<string>, limpiar: () => void, salen = true) {
  const qc = useQueryClient();
  const { yo } = useSession();
  return async (data: Record<string, unknown>, aviso: string) => {
    const ids = [...sel];
    if (!ids.length) return;
    try {
      await actualizarCasos(ids, data);
      if (data.estado === 'CERRADO') for (const id of ids) quitarCaso(yo.miembroId, id);
      if (data.estado === 'CERRADO') await despejarCerrados(qc, ids, salen);
      toast(aviso.replace(/\{n\}/g, String(ids.length)).replace(/\{s\}/g, ids.length === 1 ? '' : 's'));
      limpiar();
    } catch (e) {
      toast((e as Error).message, 'error');
    }
    refrescarCasos(qc);
  };
}

type EnLote = ReturnType<typeof useEnLote>;

function BarraLote({ sel, total, todos, limpiar, enLote, children, pedirCierre = 0, dePagina = false }: { sel: Set<string>; total: number; todos: () => void; limpiar: () => void; enLote: EnLote; children?: ReactNode; pedirCierre?: number; dePagina?: boolean }) {
  const { yo } = useSession();
  const [confirmar, setConfirmar] = useState(false);
  // La tecla E de la lista pide cerrar lo seleccionado: abre la misma confirmacion que el boton.
  useEffect(() => {
    if (pedirCierre) setConfirmar(true);
  }, [pedirCierre]);
  const [trabajando, setTrabajando] = useState(false);
  const n = sel.size;
  const plural = n === 1 ? '' : 's';
  const correr = async (data: Record<string, unknown>, aviso: string) => {
    setTrabajando(true);
    await enLote(data, aviso);
    setTrabajando(false);
    setConfirmar(false);
  };
  return (
    <div className="lote lote-sel" role="toolbar" aria-label="Acciones con los casos seleccionados">
      <span className="lote-n">
        <b>{n ? `${n} seleccionado${plural}` : 'Marca los casos que quieres cerrar'}</b>
        {n < total ? (
          <button type="button" className="linkish" onClick={todos}>
            Seleccionar los {total}
            {dePagina ? ' de esta página' : ''}
          </button>
        ) : null}
        {n ? (
          <button type="button" className="linkish" onClick={limpiar}>
            Quitar selección
          </button>
        ) : null}
      </span>
      {yo.miembroId ? (
        <button type="button" className="btn sm" disabled={!n || trabajando} onClick={() => void correr({ asignadoId: yo.miembroId }, '{n} caso{s} ahora son tuyos.')}>
          <Icon name="user" /> Lo tomo
        </button>
      ) : null}
      <button type="button" className="btn sm primary" disabled={!n || trabajando} onClick={() => setConfirmar(true)}>
        <Icon name="check" /> Cerrar{n ? ` ${n}` : ''}
      </button>
      {children}
      {confirmar ? (
        <Dialog titulo={`¿Cerrar ${n} caso${plural}?`} subtitulo="Salen de Mi día y de las vistas abiertas. No se le avisa al cliente; si vuelve a escribir, el caso se reabre." onClose={() => setConfirmar(false)} width={460}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn" onClick={() => setConfirmar(false)}>
              Cancelar
            </button>
            <button type="button" className="btn primary" disabled={trabajando} onClick={() => void correr({ estado: 'CERRADO', cerradoEn: new Date().toISOString(), seguimientoEn: null }, '{n} caso{s} cerrado{s}.')}>
              {trabajando ? 'Cerrando…' : `Cerrar ${n}`}
            </button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function useSeleccion() {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const marcar = (ids: string[], si: boolean) =>
    setSel((x) => {
      const n = new Set(x);
      for (const id of ids) {
        if (si) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  return { sel, setSel, marcar, limpiar: () => setSel(new Set()) };
}

function Marca({ etiqueta, si, cambiar }: { etiqueta: string; si: boolean; cambiar: (si: boolean) => void }) {
  return <input type="checkbox" className="marca-caso" aria-label={etiqueta} checked={si} onChange={(e) => cambiar(e.target.checked)} />;
}

function TarjetaCaso({ c, abrir, tomar, marca }: { c: Caso; abrir: (id: string) => void; tomar?: (c: Caso) => void; marca?: ReactNode }) {
  const nombre = useNombreMiembro();
  const s = senalDe(c);
  const { yo } = useSession();
  const nuevo = sinVer(c, useVistos(yo.miembroId));
  return (
    <article className={`caso-tarjeta ${s ? `s-${s}` : ''} ${nuevo ? 'sin-ver' : ''} ${marca ? 'con-marca' : ''}`} data-caso={c.id}>
      {marca}
      <button type="button" className="ct-abrir" onClick={() => abrir(c.id)}>
        <span className="ct-top">
          <b>{clienteDe(c)}</b>
          {c.persona && c.empresa ? <span className="ct-sub">{c.persona.nombre}</span> : null}
          <UltimaActividad c={c} nuevo={nuevo} />
        </span>
        <span className="ct-as">{c.asunto}</span>
        {c.resumenIA ? (
          <span className="ct-ia">
            <Icon name="spark" />
            {c.resumenIA}
          </span>
        ) : null}
      </button>
      <div className="ct-pie">
        <span className="caso-n">#{c.numero ?? '—'}</span>
        <Pill>{c.grupo || 'Sin grupo'}</Pill>
        <AvisoCaso c={c} s={s} />
        {c.estado === 'PENDIENTE' && c.seguimientoEn && s !== 'seguimiento' ? (
          <Pill tone="warn">
            <Icon name="clock" /> {new Date(c.seguimientoEn).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
          </Pill>
        ) : null}
        <span className="ct-quien">{quienEs(c, nombre)}</span>
        {tomar && !c.asignadoId ? (
          <button type="button" className="btn sm" onClick={() => tomar(c)}>
            Lo tomo
          </button>
        ) : null}
      </div>
    </article>
  );
}

function useAbrirCaso() {
  const ir = useNavigate();
  return (id: string) => {
    const n = new URLSearchParams(window.location.search);
    n.set('caso', id);
    ir(`/bandeja/casos?${n.toString()}`);
  };
}

/* ---------------- Mi dia ---------------- */

// Arriba de Mi dia: pedirle algo al asistente sin salir (abre la ventana flotante con el pedido).
function PideAlAsistente() {
  const [t, setT] = useState('');
  const pedir = (x: string) => {
    if (!x.trim()) return;
    abrirAsistente(x.trim());
    setT('');
  };
  return (
    <form className="pide" onSubmit={(e) => (e.preventDefault(), pedir(t))}>
      <Icon name="spark" />
      <input className="inp" value={t} onChange={(e) => setT(e.target.value)} placeholder="Pídele algo al asistente: “¿qué renovaciones vencen pronto?”" aria-label="Pedirle algo al asistente" />
      <button type="button" className="btn sm" onClick={() => pedir('Ordena mi día: dime qué atender primero y por qué.')}>
        Ordenar mi día
      </button>
      <button className="btn sm primary" disabled={!t.trim()}>
        Pedir
      </button>
    </form>
  );
}

export function MiDia() {
  const [f] = useFiltros();
  const { yo } = useSession();
  const r = useCasosSinCerrar();
  const busqueda = useBusqueda(f.q);
  const filtrados = useMemo(() => {
    const encontrados = busqueda.data ? new Set(busqueda.data.ids) : undefined;
    return (r.data ?? []).filter((c) => pasaFiltros(c, f, yo.miembroId, encontrados));
  }, [r.data, f, yo.miembroId, busqueda.data]);
  const d = useMiDia(filtrados);
  const abrir = useAbrirCaso();
  const tomar = useTomar();
  const grupos = useMisGrupos();
  // Seleccionar varios para cerrarlos o tomarlos (D53).
  const [eligiendo, setEligiendo] = useState(false);
  const s = useSeleccion();
  const enLote = useEnLote(s.sel, s.limpiar);
  const visibles = [...d.respondieron, ...d.seguimientos, ...d.nuevosMios, ...d.nuevosOtros];
  if (r.error) return <ErrorBox error={r.error} onRetry={() => r.refetch()} />;
  if (r.isLoading || busqueda.isLoading) return <Skeleton rows={8} />;
  const seccion = (titulo: string, tono: string, xs: Caso[], vacio: string, conTomar = false, extra?: ReactNode) => (
    <section className="dia-sec">
      <div className="dia-h">
        {eligiendo && xs.length ? (
          <Marca etiqueta={`Seleccionar todos en ${titulo}`} si={xs.every((c) => s.sel.has(c.id))} cambiar={(si) => s.marcar(xs.map((c) => c.id), si)} />
        ) : (
          <span className={`dia-punto ${tono}`} aria-hidden="true" />
        )}
        <h2>{titulo}</h2>
        <span className="cnt">{xs.length}</span>
        {extra}
      </div>
      {xs.length ? (
        xs.map((c) => (
          <TarjetaCaso
            key={c.id}
            c={c}
            abrir={abrir}
            tomar={conTomar && !eligiendo ? tomar : undefined}
            marca={eligiendo ? <Marca etiqueta={`Seleccionar caso ${c.numero ?? ''}`} si={s.sel.has(c.id)} cambiar={(si) => s.marcar([c.id], si)} /> : undefined}
          />
        ))
      ) : (
        <p className="dia-vacio">{vacio}</p>
      )}
    </section>
  );
  return (
    <>
      <PideAlAsistente />
      {eligiendo ? (
        <BarraLote sel={s.sel} total={visibles.length} todos={() => s.marcar(visibles.map((c) => c.id), true)} limpiar={s.limpiar} enLote={enLote}>
          <button type="button" className="btn sm ghost" onClick={() => (setEligiendo(false), s.limpiar())}>
            Listo
          </button>
        </BarraLote>
      ) : visibles.length ? (
        <div className="dia-acciones">
          <button type="button" className="btn sm" onClick={() => setEligiendo(true)}>
            <Icon name="check" /> Seleccionar varios
          </button>
        </div>
      ) : null}
      {!visibles.length && !hayFiltros(f) ? (
        <BandejaDespejada />
      ) : (
        <div className="dia-grid">
          {seccion('Te respondieron', 'acc', d.respondieron, hayFiltros(f) ? 'Nada con estos filtros.' : 'Nadie te ha escrito todavía.')}
          {seccion('Toca hacer seguimiento', 'warn', d.seguimientos, 'No hay clientes que perseguir hoy.')}
          {seccion(
            grupos.miembro.length ? 'Nuevos en mis grupos' : 'Nuevos',
            'text',
            d.nuevosMios,
            grupos.miembro.length ? 'No entró nada nuevo a tus grupos.' : 'No eres miembro de ningún grupo: pide que te agreguen en Administración → Grupos.',
            true,
          )}
          {seccion(
            'Nuevos en otros grupos',
            'faint',
            d.nuevosOtros,
            'Los demás grupos no tienen casos sin tomar.',
            true,
            d.esperaOtros ? (
              <span className="senal s-espera dia-ayuda">
                <span className="punto" aria-hidden="true" />
                {d.esperaOtros} esperan respuesta en otros grupos
              </span>
            ) : null,
          )}
        </div>
      )}
    </>
  );
}

/* ---------------- busqueda (D45) ---------------- */

// Lo que encontro el buscador para la busqueda de la barra (la misma consulta que usan la lista y el conteo).
function useBusqueda(q: string) {
  const t = q.trim();
  return useQuery({ queryKey: ['buscar-en-casos', t.toLowerCase()], queryFn: () => buscarEnCasos(t), enabled: !!t, staleTime: 15_000 });
}

// Fragmento del mensaje donde coincidio la busqueda; el buscador marca las palabras entre « y ».
function Fragmento({ texto }: { texto: string }) {
  return (
    <>
      {texto.split(/(«[^»]*»)/).map((p, i) => (p.startsWith('«') && p.endsWith('»') ? <mark key={i}>{p.slice(1, -1)}</mark> : p))}
    </>
  );
}

/* ---------------- lista de una vista ---------------- */

export function ListaCasos({ elegida }: { elegida: Exclude<VistaElegida, { tipo: 'dia' }> }) {
  const [f] = useFiltros();
  const { yo } = useSession();
  const grupos = useMisGrupos();
  const nombre = useNombreMiembro();
  const abrir = useAbrirCaso();
  const qc = useQueryClient();
  const ir = useNavigate();
  const huerfanos = elegida.tipo === 'huerfanos';
  const vistos = useVistos(yo.miembroId);
  // "Todos" sin estado elegido muestra los sin cerrar (los cerrados son miles: se piden con "Resueltos").
  const vista: VistaCaso = elegida.tipo === 'guardada' ? elegida.vista : huerfanos ? VISTA_SIN_CERRAR : f.estado ? VISTA_TODOS : VISTA_SIN_CERRAR;
  const ctx = { miembroId: yo.miembroId, grupo: f.grupo, q: f.q, estado: f.estado, quien: f.quien, gruposPermitidos: grupos.permitidos, gruposExistentes: grupos.existentes, huerfanos };
  const clave = [vista.id, f.grupo, f.q, f.estado, f.quien, yo.miembroId, grupos.permitidos.join(','), grupos.existentes.join(','), huerfanos];
  // Orden y pagina (en la direccion): los hace el servidor, asi valen para todos los casos de la vista.
  const [o, cambiarOrden] = useOrdenCasos();
  const total = useQuery({ queryKey: ['vistas-conteo', 'lista', ...clave], queryFn: () => contarVista(vista, ctx), enabled: !grupos.cargando, staleTime: 15_000 });
  // La pagina es una lista simple de casos (las acciones rapidas la actualizan como a las demas listas). Mientras llega
  // la pagina siguiente se sigue viendo la anterior, atenuada.
  const r = useQuery({
    queryKey: ['casos-vista', 'lista', ...clave, o.campo, o.dir, o.explicito, o.tam, o.pag],
    queryFn: async () => (await paginaDeVista<RawCaso>(vista, ctx, o, total.data ?? null)).map(aCaso),
    enabled: !grupos.cargando && (o.pag === 1 || total.data != null),
    placeholderData: keepPreviousData,
    refetchInterval: 2 * 60_000,
    staleTime: 15_000,
  });
  const paginas = total.data != null ? totalPaginas(total.data, o.tam) : null;
  // Una pagina que ya no existe (menos casos con estos filtros) lleva a la ultima.
  useEffect(() => {
    if (paginas != null && o.pag > paginas) cambiarOrden({ pag: paginas });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginas, o.pag]);
  const busqueda = useBusqueda(f.q);
  const { sel, setSel, marcar, limpiar } = useSeleccion();
  // Los casos cerrados salen de la vista si esta solo muestra estados abiertos (no en "Resueltos").
  const estadosVista = f.estado ? ESTADOS_FILTRO[f.estado] : vista.estados;
  const cerradosSalen = estadosVista.length > 0 && !estadosVista.includes('CERRADO');
  const cerrarEnLote = useEnLote(sel, limpiar, cerradosSalen);
  const [destino, setDestino] = useState('');
  const lista = r.data ?? [];

  // Teclado: J / K mueven el cursor por la lista, E cierra lo seleccionado (o el caso del cursor) con la misma
  // confirmacion del boton, Enter abre el caso del cursor. No actua escribiendo, en dialogos ni durante una llamada.
  const [cursor, setCursor] = useState(-1);
  const [pedirCierre, setPedirCierre] = useState(0);
  const filasRef = useRef<HTMLDivElement>(null);
  // Otra pagina, otro orden u otros filtros: la seleccion y el cursor son de la pagina que se ve.
  const firmaPagina = JSON.stringify([...clave, o.campo, o.dir, o.tam, o.pag]);
  useEffect(() => {
    limpiar();
    setCursor(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaPagina]);
  const irAPagina = (pag: number) => {
    cambiarOrden({ pag });
    document.querySelector('.casos-lista')?.scrollIntoView({ block: 'start' });
  };
  useKey((e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || huerfanos || !lista.length) return;
    const t = e.target as HTMLElement | null;
    const escribiendo = !!t && ((t.tagName === 'INPUT' && (t as HTMLInputElement).type !== 'checkbox') || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    if (escribiendo || !filasRef.current?.offsetParent || document.querySelector('.dialog, .sf-pop, .cmdk') || phone.getState().call) return;
    const k = e.key.toLowerCase();
    if (k === 'j' || k === 'k') {
      e.preventDefault();
      setCursor((x) => Math.max(0, Math.min(lista.length - 1, x < 0 ? 0 : x + (k === 'j' ? 1 : -1))));
    } else if (k === 'e') {
      const actual = lista[cursor];
      if (!sel.size && !actual) return;
      e.preventDefault();
      if (!sel.size && actual) setSel(new Set([actual.id]));
      setPedirCierre((x) => x + 1);
    } else if (e.key === 'Enter' && (t === document.body || t === filasRef.current) && lista[cursor]) {
      e.preventDefault();
      abrir(lista[cursor].id);
    }
  });
  const idCursor = lista[cursor]?.id;
  useEffect(() => {
    if (idCursor) document.querySelector(`[data-caso="${CSS.escape(idCursor)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [idCursor]);
  const mia = elegida.tipo === 'guardada' && elegida.vista.miembros.length === 1 && elegida.vista.miembros[0] === yo.miembroId;

  const enLote = async (data: Record<string, unknown>, aviso: string) => {
    const ids = [...sel];
    try {
      await actualizarCasos(ids, data);
      if (data.estado === 'CERRADO') await despejarCerrados(qc, ids, true);
      toast(aviso.replace('{n}', String(ids.length)));
      setSel(new Set());
      refrescarCasos(qc);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  const borrar = async () => {
    if (elegida.tipo !== 'guardada') return;
    try {
      await borrarVista(elegida.vista.id);
      await qc.invalidateQueries({ queryKey: ['vistas-caso'] });
      toast(`Vista "${elegida.vista.nombre}" borrada.`);
      ir('/bandeja/casos');
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  return (
    <div className="casos-lista">
      <div className="cl-resumen">
        {total.data != null ? <ResumenPagina pag={o.pag} tam={o.tam} total={total.data} /> : null}
        {!huerfanos && lista.length ? (
          <span className="atajos-casos" aria-hidden="true">
            <kbd>J</kbd> <kbd>K</kbd> moverte · <kbd>E</kbd> cerrar · <kbd>Enter</kbd> abrir
          </span>
        ) : null}
        {mia ? (
          <button type="button" className="btn sm ghost" onClick={() => void borrar()}>
            <Icon name="trash" /> Borrar esta vista
          </button>
        ) : null}
      </div>
      {!huerfanos && lista.length ? (
        <BarraLote sel={sel} total={lista.length} todos={() => marcar(lista.map((c) => c.id), true)} limpiar={limpiar} enLote={cerrarEnLote} pedirCierre={pedirCierre} dePagina={(paginas ?? 1) > 1} />
      ) : null}
      {huerfanos ? (
        <div className="lote">
          <span>
            Casos de grupos que no existen o están inactivos en el CRM. Nadie más los ve: muévelos a un grupo o ciérralos.
            {sel.size ? ` ${sel.size} seleccionados.` : ''}
          </span>
          <select className="inp" value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Grupo destino">
            <option value="">Mover a…</option>
            {grupos.nombres.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
          <button type="button" className="btn sm" disabled={!sel.size || !destino} onClick={() => void enLote({ grupo: destino }, `{n} casos pasaron a ${destino}.`)}>
            Mover
          </button>
          <button type="button" className="btn sm" disabled={!sel.size} onClick={() => void enLote({ estado: 'CERRADO', cerradoEn: new Date().toISOString() }, '{n} casos cerrados.')}>
            Cerrar
          </button>
        </div>
      ) : null}
      {r.error ? <ErrorBox error={r.error} onRetry={() => r.refetch()} /> : null}
      {r.isLoading ? (
        <Skeleton rows={10} />
      ) : lista.length ? (
        <div className={`filas-casos con-sel ${r.isPlaceholderData ? 'cargando' : ''}`} role="list" ref={filasRef} aria-busy={r.isFetching && r.isPlaceholderData}>
          <CabeceraCasos orden={o} ordenar={(campo) => cambiarOrden(siguienteOrden(o, campo))} todas={lista.every((c) => sel.has(c.id))} marcarTodas={(si) => marcar(lista.map((c) => c.id), si)} />
          {lista.map((c, i) => {
            const s = senalDe(c);
            const nuevo = sinVer(c, vistos);
            return (
              <div key={c.id} className={`fila-caso ${s ? `s-${s}` : ''} ${nuevo ? 'sin-ver' : ''} ${i === cursor ? 'cursor' : ''}`} role="listitem" data-caso={c.id} aria-current={i === cursor ? 'true' : undefined}>
                <Marca etiqueta={`Seleccionar caso ${c.numero ?? ''}`} si={sel.has(c.id)} cambiar={(si) => marcar([c.id], si)} />
                <button type="button" className="fc-abrir" onClick={() => abrir(c.id)}>
                  <span className="punto" aria-hidden="true" />
                  <span className="caso-n fc-num">#{c.numero ?? '—'}</span>
                  <span className="fc-txt">
                    <span className="fc-l1">
                      <b>{clienteDe(c)}</b> <span className="fc-as">{c.asunto}</span>
                    </span>
                    {busqueda.data?.fragmentos.get(c.id) ? (
                      <span className="fc-l2 fc-frag">
                        <Fragmento texto={busqueda.data.fragmentos.get(c.id) as string} />
                      </span>
                    ) : (
                      <span className="fc-l2">{s ? textoSenal(c, s) : c.resumenIA || c.correoCliente || nombreEstado(c.estado)}</span>
                    )}
                  </span>
                  <span className="fc-col">
                    <Pill>{c.grupo || 'Sin grupo'}</Pill>
                  </span>
                  <span className="fc-col">{quienEs(c, nombre)}</span>
                  <span className="fc-col fc-est">
                    {c.estado === 'PENDIENTE' && c.seguimientoEn ? `Seguir ${new Date(c.seguimientoEn).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}` : nombreEstado(c.estado)}
                  </span>
                  <UltimaActividad c={c} nuevo={nuevo} creado={o.campo === 'creado'} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        !hayFiltros(f) && !huerfanos && cerradosSalen && o.pag === 1 ? (
          <BandejaDespejada />
        ) : (
          <Empty icon="check" titulo={hayFiltros(f) ? 'Nada con estos filtros' : 'Nada en esta vista'} />
        )
      )}
      {total.data != null && total.data > TAMANOS[0] ? <Paginador pag={o.pag} paginas={paginas ?? 1} tam={o.tam} ir={irAPagina} cambiarTam={(tam) => cambiarOrden({ tam })} cargando={r.isFetching && r.isPlaceholderData} /> : null}
    </div>
  );
}

/* ---------------- orden y paginas (cabecera de columnas y paginador) ---------------- */

const miles = (n: number) => n.toLocaleString('es-CO');

// "Casos 301–400 de 21.955" (con una sola pagina, "24 casos").
function ResumenPagina({ pag, tam, total }: { pag: number; tam: number; total: number }) {
  if (total <= tam) return <span>{`${miles(total)} ${total === 1 ? 'caso' : 'casos'}`}</span>;
  const { desde, hasta } = rangoPagina(pag, tam, total);
  return (
    <span>
      Casos <b className="cl-rango">{`${miles(desde)}–${miles(hasta)}`}</b> de {miles(total)}
    </span>
  );
}

// Columnas que se pueden ordenar, alineadas con la fila (marca | # | cliente y asunto | grupo | asignado | estado | cuando).
const COLUMNAS: [CampoOrden, string, string][] = [
  ['numero', 'Número', 'número'],
  ['asunto', 'Cliente · asunto', 'asunto'],
  ['grupo', 'Grupo', 'grupo'],
  ['asignado', 'Asignado', 'asignado (junta los casos de cada persona)'],
  ['estado', 'Estado', 'estado'],
];
const NOMBRE_CAMPO: Record<CampoOrden, string> = { fecha: 'Última actividad', creado: 'Fecha de creación', numero: 'Número', asunto: 'Asunto', grupo: 'Grupo', asignado: 'Asignado', estado: 'Estado' };

function BotonOrden({ campo, texto, ayuda, orden, ordenar }: { campo: CampoOrden; texto: string; ayuda: string; orden: OrdenCasos; ordenar: (c: CampoOrden) => void }) {
  const activo = orden.campo === campo;
  const sentido = orden.dir === 'asc' ? 'ascendente' : 'descendente';
  return (
    <button type="button" className={`cab-orden ${activo ? 'activo' : ''}`} onClick={() => ordenar(campo)} aria-pressed={activo} title={activo ? `Ordenado por ${ayuda}, ${sentido}. Clic para invertir.` : `Ordenar por ${ayuda}`}>
      <span>{texto}</span>
      {activo ? <Icon name={orden.dir === 'asc' ? 'chevUp' : 'chevDown'} /> : null}
    </button>
  );
}

function CabeceraCasos({ orden, ordenar, todas, marcarTodas }: { orden: OrdenCasos; ordenar: (c: CampoOrden) => void; todas: boolean; marcarTodas: (si: boolean) => void }) {
  return (
    <div className="cab-casos">
      <Marca etiqueta="Seleccionar los casos de esta página" si={todas} cambiar={marcarTodas} />
      <div className="cab-grid" role="group" aria-label="Ordenar la lista por columna">
        <span aria-hidden="true" />
        {COLUMNAS.map(([campo, texto, ayuda]) => (
          <BotonOrden key={campo} campo={campo} texto={texto} ayuda={ayuda} orden={orden} ordenar={ordenar} />
        ))}
        <span className="cab-cuando">
          <BotonOrden campo="fecha" texto="Actividad" ayuda="última actividad" orden={orden} ordenar={ordenar} />
          <BotonOrden campo="creado" texto="Creado" ayuda="fecha de creación" orden={orden} ordenar={ordenar} />
        </span>
      </div>
      {/* En el celular las columnas no se ven: el orden se elige de una lista. */}
      <div className="cab-movil">
        <label>
          <span>Ordenar por</span>
          <select className="inp" value={orden.campo} onChange={(e) => ordenar(e.target.value as CampoOrden)} aria-label="Ordenar por">
            {(Object.keys(NOMBRE_CAMPO) as CampoOrden[]).map((c) => (
              <option key={c} value={c}>
                {NOMBRE_CAMPO[c]}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn sm" onClick={() => ordenar(orden.campo)} aria-label={`Invertir el orden (ahora ${orden.dir === 'asc' ? 'ascendente' : 'descendente'})`}>
          <Icon name={orden.dir === 'asc' ? 'chevUp' : 'chevDown'} />
          {orden.dir === 'asc' ? 'Ascendente' : 'Descendente'}
        </button>
      </div>
    </div>
  );
}

function Paginador({ pag, paginas, tam, ir, cambiarTam, cargando }: { pag: number; paginas: number; tam: number; ir: (p: number) => void; cambiarTam: (t: number) => void; cargando: boolean }) {
  return (
    <nav className="cl-paginas" aria-label="Páginas de casos">
      {paginas > 1 ? (
        <div className="cl-pags">
          <button type="button" className="btn sm" disabled={pag <= 1} onClick={() => ir(pag - 1)}>
            <Icon name="chevLeft" /> Anterior
          </button>
          {paginasVisibles(pag, paginas).map((x, i) =>
            x === '…' ? (
              <span key={`s${i}`} className="cl-salto" aria-hidden="true">
                …
              </span>
            ) : (
              <button key={x} type="button" className={`cl-pag ${x === pag ? 'actual' : ''}`} aria-current={x === pag ? 'page' : undefined} aria-label={`Página ${x}`} onClick={() => x !== pag && ir(x)}>
                {miles(x)}
              </button>
            ),
          )}
          <button type="button" className="btn sm" disabled={pag >= paginas} onClick={() => ir(pag + 1)}>
            Siguiente <Icon name="chevRight" />
          </button>
          {cargando ? <span className="cl-cargando">Cargando…</span> : null}
        </div>
      ) : (
        <span />
      )}
      <label className="cl-tam">
        <span>Por página</span>
        <select className="inp" value={tam} onChange={(e) => cambiarTam(Number(e.target.value))}>
          {TAMANOS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}
